import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useCustomers } from "../../context/CustomerContext";
import * as XLSX from "xlsx";
import { FiX } from "react-icons/fi";
import { useSparrowCredit } from "../../context/SparrowCreditContext";

const ComposeSMS = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { customers } = useCustomers();

    const { fetchCredit, creditInfo } = useSparrowCredit();

    const [recipients, setRecipients] = useState([]);
    const [recipientInput, setRecipientInput] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [body, setBody] = useState("");
    const [sampleSize, setSampleSize] = useState(100);
    const [smsResult, setSmsResult] = useState(null);

    const token = localStorage.getItem("token");
    const inputRef = useRef(null);

    // Filter customers
    useEffect(() => {
        if (!recipientInput.trim()) {
            setFilteredCustomers([]);
            return;
        }
        const val = recipientInput.toLowerCase();
        setFilteredCustomers(
            customers.filter(
                c =>
                    c.name.toLowerCase().includes(val) ||
                    (c.phone || "").toLowerCase().includes(val)
            )
        );
    }, [recipientInput, customers]);

    const addRecipient = (customer) => {
        const value = customer.phone;
        if (!value || recipients.some(r => r.value === value)) return;

        setRecipients(prev => [...prev, { name: customer.name, value, raw: customer }]);
        setRecipientInput("");
        setFilteredCustomers([]);
    };

    const addTypedRecipient = () => {
        const raw = recipientInput.trim();
        if (!raw) return;

        if (!/^\d+$/.test(raw)) {
            toast.error("Invalid phone number");
            return;
        }

        addRecipient({ name: raw, phone: raw });
    };

    const handleRecipientKey = (e) => {
        if (["Enter", "Tab", ","].includes(e.key)) {
            e.preventDefault();
            addTypedRecipient();
        }
    };

    const removeRecipient = (value) => {
        setRecipients(prev => prev.filter(r => r.value !== value));
    };

    const handleSend = async () => {
        if (sampleSize < recipients.length) {
            toast.error(
                `Number of customers must be at least equal to selected recipients (${recipients.length}).`
            );
            return;
        }

        if (creditInfo && Number(sampleSize) > Number(creditInfo.credits_available)) {
            toast.error(
                `Not enough credits. Available: ${creditInfo.credits_available}`
            );
            return;
        }

        const payload = {
            token,
            text: body,
            extra_numbers: recipients.map(r => r.value),
            sample_size: Number(sampleSize)
        };

        try {
            const { data } = await axios.post(`${baseUrl}/send-bulk-sms`, payload);

            toast.success("SMS sent successfully");
            setRecipients([]);
            setBody("");
            setSampleSize(100);
            setSmsResult(data);

            setTimeout(() => {
                fetchCredit();
            }, 4000);
        } catch (err) {
            console.error(err);
            toast.error("Failed to send SMS");
        }
    };

    const exportToExcel = () => {
        if (!smsResult?.recipients) return;

        const worksheet = XLSX.utils.json_to_sheet(smsResult.recipients);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Recipients");

        XLSX.writeFile(workbook, "sms_recipients.xlsx");
    };

    return (
        <div className="p-5 bg-white shadow rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Bulk SMS</h2>

            {smsResult && (
                <div className="border p-4 bg-gray-50 rounded-lg mb-4 relative shadow-md text-sm space-y-2">
                    {/* Close button */}
                    <button
                        className="absolute top-2 right-2 text-red-500 border bg-white p-1 rounded-md hover:bg-gray-50 hover:text-red-600 hover:shadow-md"
                        onClick={() => setSmsResult(null)}
                    >
                        <FiX size={16} />
                    </button>

                    <h3 className="font-semibold text-lg">SMS Result</h3>

                    <div className="flex justify-between items-center">
                        <div>
                            <strong>Total Recipients:</strong> {smsResult.recipients?.length || 0}
                        </div>

                        {/* Export button */}
                        <button
                            onClick={exportToExcel}
                            className="px-4 py-2 font-semibold rounded hover:shadow-md border text-xs bg-white hover:bg-gray-100 text-blue-500 hover:text-blue-600"
                        >
                            Export to Excel
                        </button>
                    </div>


                    <div className="border rounded p-3 bg-white max-h-[350px] overflow-y-auto">
                        <div className="grid grid-cols-2 gap-3">
                            {smsResult.recipients?.map((r, i) => (
                                <div key={i} className="p-1 border-b text-sm flex justify-between">
                                    <span className="font-medium">{i + 1}. {r.name}</span>
                                    <span className="text-gray-600">{r.phone}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="mb-4">
                <label className="text-sm text-gray-600">To</label>
                <div className="border rounded px-2 py-2 mt-1 relative">
                    <div className="flex flex-wrap gap-2">
                        {recipients.map(r => (
                            <span
                                key={r.value}
                                className="flex items-center gap-2 bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-sm"
                            >
                                {r.name} ({r.value})
                                <button onClick={() => removeRecipient(r.value)} className="text-xs">✕</button>
                            </span>
                        ))}

                        <input
                            ref={inputRef}
                            value={recipientInput}
                            onChange={e => setRecipientInput(e.target.value)}
                            onKeyDown={handleRecipientKey}
                            placeholder="Type name or phone"
                            className="flex-1 min-w-[180px] outline-none px-1 py-1"
                        />
                    </div>

                    {filteredCustomers.length > 0 && (
                        <div className="absolute left-0 right-0 top-full bg-white border shadow mt-1 rounded z-10 max-h-48 overflow-auto">
                            {filteredCustomers.map(c => (
                                <div
                                    key={c.id}
                                    onClick={() => addRecipient(c)}
                                    className="px-3 py-2 hover:bg-indigo-50 cursor-pointer"
                                >
                                    <div className="font-medium">{c.name}</div>
                                    <div className="text-sm text-gray-600">{c.phone}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-4">
                <label className="text-sm text-gray-600">Number of customers</label>
                <input
                    type="number"
                    min="1"
                    value={sampleSize}
                    onChange={e => setSampleSize(e.target.value)}
                    className="w-full border rounded px-3 py-2 mt-1"
                />
            </div>

            <div className="mb-4">
                <label className="text-sm text-gray-600">Message</label>
                <textarea
                    rows={5}
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    className="w-full border rounded px-3 py-2 mt-1"
                />
            </div>

            <button
                onClick={handleSend}
                className="px-4 py-2 rounded-sm bg-purple-100 text-purple-800 hover:bg-purple-200 text-sm"
            >
                Send SMS
            </button>
        </div>
    );
};

export default ComposeSMS;
